// =============================================================================
// Optim TURPE V2 — CE QUE LA PAGE RÉSERVE À L'ÉQUIPE, ET L'AIDE DU CLIENT.
//
// Décision du 05/10/2026 (DEV-2311) : « Faire relire cette étude » est retiré
// de la page d'un client, parce qu'il exposait des détails techniques
// (identifiant de l'étude, code de la cause, versions des composants). Le
// dossier technique reste lisible d'une session de l'équipe seulement.
//
// « L'équipe » = la mention `staff` que le serveur SIGNE dans le jeton de
// session à une connexion au mot de passe maître (DEV-2306), lue comme
// l'interrupteur la lit (`core/stores/interrupteurs.store.ts`). Jamais
// `adminHomeys` (stockage du navigateur, falsifiable), jamais le rôle
// d'administrateur (c'est l'administrateur de la société cliente). Fermé par
// défaut : sans magasin d'authentification, sans jeton, ce n'est pas l'équipe.
// =============================================================================

import { DEFAULT_SCOPE_NAME } from "@core/constants"
import { useAuthStore } from "@core/stores/auth.store"
import { sessionDeLEquipeSelonLeJeton } from "@core/stores/auth.staff"

/** Vrai pour une session de l'équipe (mention signée du jeton). Ne lève jamais. */
export function estSessionDeLEquipe(): boolean {
    try {
        return sessionDeLEquipeSelonLeJeton(useAuthStore().token)
    } catch {
        return false
    }
}

/**
 * Vrai quand la session voit le bouton « Aide » de l'application : la même
 * condition que `core/components/support/SupportButton.vue` (employé en
 * écriture d'une société cliente en direct). Le client d'un courtier en marque
 * blanche ne le voit pas : la page ne lui promet donc pas d'aide qu'il n'a pas.
 */
export function aideDeLApplicationDisponible(): boolean {
    try {
        const auth = useAuthStore()
        return Boolean(auth.isEmployeWriteOrHigher && auth.isClientHomeys)
    } catch {
        return false
    }
}

/** Ce que la page lit du scope : son nom, et l'adresse de contact s'il en sert une. */
export type ScopeContact = {
    name?: string | null
    mail?: { support?: string | null } | null
} | null

/**
 * Vrai pour un scope de marque blanche CONNU (05/10/2026) : un nom servi, autre
 * que le scope par défaut. Un scope non encore chargé n'est pas présumé en
 * marque blanche.
 */
export function estScopeDeMarqueBlanche(
    scope: ScopeContact | undefined
): boolean {
    const nom = scope?.name ?? null
    return Boolean(nom) && nom !== DEFAULT_SCOPE_NAME
}

const COURRIEL_VALIDE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
/**
 * Ce qu'une adresse de contact ne porte jamais (05/10/2026) : un caractère qui
 * ferait du lien `mailto:` autre chose qu'une adresse seule (paramètres
 * `?bcc=`, `&body=`, nom entre guillemets ou chevrons, liste par virgule ou
 * point-virgule), ni une espace.
 */
const CARACTERES_REFUSES = /[?&"<>,;\s]/

/**
 * L'adresse de contact d'un scope de marque blanche, ou `null` (05/10/2026).
 * Le champ public `mail.support` du scope, déjà lu avant le 05/10 pour
 * « Écrire au support » (`optim-turpe-v2.relais.ts`, retiré par la PR 704).
 * Jamais sur le scope par défaut, jamais une adresse invalide ni porteuse
 * d'un caractère refusé (`CARACTERES_REFUSES`), jamais une adresse de
 * l'éditeur (la marque blanche ne le nomme pas).
 */
export function adresseDeContactDuScope(
    scope: ScopeContact | undefined
): string | null {
    if (!estScopeDeMarqueBlanche(scope)) return null
    const adresse = scope?.mail?.support?.trim() ?? ""
    if (!COURRIEL_VALIDE.test(adresse) || CARACTERES_REFUSES.test(adresse)) {
        return null
    }
    return /homeys/i.test(adresse) ? null : adresse
}
